import { Directory, Encoding, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';

import { isCapacitorNative } from '@/core/utils/capacitor.utils';

const BLOB_REVOKE_MS = 1_000;

/**
 * Download or share a CSV string (UTF-8 with optional BOM already in `csv`).
 * Native: write to Cache + Share sheet; browser: Blob + anchored download.
 */
export async function downloadCsvFile(
  csv: string,
  filename: string,
): Promise<void> {
  if (isCapacitorNative()) {
    try {
      const writeResult = await Filesystem.writeFile({
        path: filename,
        data: csv,
        directory: Directory.Cache,
        encoding: Encoding.UTF8,
      });
      await Share.share({
        title: filename,
        text: filename,
        url: writeResult.uri,
      });
      return;
    } catch (error) {
      console.error('Native CSV export failed, using browser fallback', error);
    }
  }

  triggerBrowserFileDownload(csv, filename, 'text/csv;charset=utf-8');
}

/**
 * Start a same-document file download. The anchor must be in the DOM, and the
 * blob URL must stay alive until the browser queues the download.
 */
export function triggerBrowserFileDownload(
  contents: string,
  filename: string,
  mimeType: string,
): void {
  const blob = new Blob([contents], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
  globalThis.setTimeout(() => {
    URL.revokeObjectURL(url);
  }, BLOB_REVOKE_MS);
}
